import { useEffect, useState, type FormEvent } from "react";
import { Button, Card, ClevisMark, Input, Label, Select, cn } from "@clevis/shared-ui";
import { call } from "../../lib/callFunction";

// Public intake form at /request/{tenantSlug} — works signed-out. A company
// shares this link (or embeds it on its own site with ?embed=1); what comes in
// lands in their Requests inbox quote-ready, sorted to the right department.
// Campaign tags on the link (utm_*, source, page) ride along for lead tracking.

type Stage = "loading" | "unavailable" | "form" | "sent";
interface FormInfo {
  companyName: string;
  serviceTypes: Array<{ id: string; label: string }>;
  askHowHeard: boolean;
  howHeardOptions: string[];
  /** Clevis Relay: who sent them, when the link carried a code. */
  referrer: { name: string; friendOffer: string } | null;
}

const params = new URLSearchParams(window.location.search);
const EMBED = params.get("embed") === "1";
const LEAD = {
  source: params.get("source") ?? "",
  utmSource: params.get("utm_source") ?? "",
  utmMedium: params.get("utm_medium") ?? "",
  utmCampaign: params.get("utm_campaign") ?? "",
  page: params.get("page") ?? "",
  ref: params.get("ref") ?? "",
};

/** Inside a company's site: tell the page around us how tall the form is. */
function useReportHeight(): void {
  useEffect(() => {
    if (!EMBED || window.parent === window) return;
    const send = () => window.parent.postMessage({ type: "clevis:height", height: document.documentElement.scrollHeight }, "*");
    const ro = new ResizeObserver(send);
    ro.observe(document.body);
    send();
    return () => ro.disconnect();
  }, []);
}

export function PublicRequestPage({ slug }: { slug: string }) {
  const [stage, setStage] = useState<Stage>("loading");
  const [companyName, setCompanyName] = useState("");
  const [info, setInfo] = useState<FormInfo | null>(null);
  useReportHeight();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    kind: "estimate",
    name: "",
    email: "",
    phone: "",
    address: "",
    details: "",
    preferredTimes: "",
    serviceType: "",
    howHeard: "",
    website: "", // honeypot — hidden from humans
  });

  useEffect(() => {
    let alive = true;
    call<FormInfo>("getRequestFormInfo", { slug, ref: LEAD.ref }, { mutation: false })
      .then((res) => {
        if (!alive) return;
        setCompanyName(res.companyName);
        setInfo({ ...res, serviceTypes: res.serviceTypes ?? [], howHeardOptions: res.howHeardOptions ?? [], askHowHeard: res.askHowHeard ?? false, referrer: res.referrer ?? null });
        setStage("form");
      })
      .catch(() => alive && setStage("unavailable"));
    return () => {
      alive = false;
    };
  }, [slug]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!form.email.trim() && !form.phone.trim()) {
      setError("Leave an email or a phone number so we can reach you.");
      return;
    }
    if (info?.serviceTypes.length && !form.serviceType) {
      setError("Pick the type of service.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await call("submitServiceRequest", { slug, ...form, ...LEAD, source: LEAD.source || (EMBED ? "website" : ""), email: form.email.trim() });
      setStage("sent");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  return (
    <div className={EMBED ? "px-1 py-1" : "min-h-full bg-cream px-4 py-8"}>
      <div className={cn("mx-auto space-y-4", EMBED ? "max-w-2xl" : "max-w-lg")}>
        {!EMBED ? (
          <div className="flex items-center gap-2">
            <ClevisMark size={28} />
            <div className="text-sm font-bold text-ink">{companyName || "Clevis"}</div>
          </div>
        ) : null}

        {stage === "loading" ? (
          <Card>
            <p className="text-sm text-steel">Loading…</p>
          </Card>
        ) : null}

        {stage === "unavailable" ? (
          <Card>
            <p className="text-sm text-ink">This request link isn't active.</p>
            <p className="mt-1 text-xs text-steel">
              Double-check the link you were given, or contact the company directly.
            </p>
          </Card>
        ) : null}

        {stage === "sent" ? (
          <Card>
            <h1 className="mb-1 text-base font-bold text-ink">Request received</h1>
            <p className="text-sm text-steel">
              Thanks, {form.name.split(" ")[0] || "there"} — {companyName} has your{" "}
              {form.kind === "estimate" ? "estimate request" : "service request"} and will get back to
              you at {form.email.trim() || form.phone}.
            </p>
          </Card>
        ) : null}

        {stage === "form" ? (
          <Card>
            <h1 className="mb-1 text-base font-bold text-ink">Request service or an estimate</h1>
            <p className="mb-4 text-xs text-steel">
              Tell {companyName} what you need and they'll get back to you.
            </p>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {(
                  [
                    { value: "estimate", label: "Get an estimate" },
                    { value: "work_order", label: "Book a service call" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={cn(
                      "rounded-lg border px-3 py-2 text-sm",
                      form.kind === opt.value
                        ? "border-brandorange bg-brandorange/10 font-semibold text-ink"
                        : "border-steel/30 bg-white text-steel hover:border-steel",
                    )}
                    onClick={() => set({ kind: opt.value })}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {info?.serviceTypes.length ? (
                <div>
                  <Label>Type of service</Label>
                  <Select required value={form.serviceType} onChange={(e) => set({ serviceType: e.target.value })}>
                    <option value="">Choose one…</option>
                    {info.serviceTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                  </Select>
                </div>
              ) : null}
              <div>
                <Label>Your name</Label>
                <Input required maxLength={120} value={form.name} onChange={(e) => set({ name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    maxLength={200}
                    value={form.email}
                    onChange={(e) => set({ email: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input type="tel" maxLength={40} value={form.phone} onChange={(e) => set({ phone: e.target.value })} />
                </div>
              </div>
              {/* A2P 10DLC disclosure: shown wherever a phone number is collected. */}
              <p className="text-[11px] leading-snug text-steel">
                By providing your phone number, you agree to receive service-related text messages from{" "}
                {companyName || "this business"} (appointment confirmations, technician on-the-way alerts, and
                invoice notices) via Clevis. Message frequency varies. Message &amp; data rates may apply. Reply
                STOP to opt out, HELP for help. Your number is never shared with third parties for marketing. See
                our{" "}
                <a className="underline" href="https://clevis.pro/privacy.html" target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a className="underline" href="https://clevis.pro/terms.html" target="_blank" rel="noreferrer">
                  SMS Terms
                </a>
                .
              </p>
              <div>
                <Label>Service address</Label>
                <Input
                  maxLength={300}
                  placeholder="Street, city"
                  value={form.address}
                  onChange={(e) => set({ address: e.target.value })}
                />
              </div>
              <div>
                <Label>What do you need done?</Label>
                <textarea
                  required
                  maxLength={2000}
                  rows={4}
                  className="w-full rounded-lg border border-steel/40 bg-white px-3 py-2 text-sm text-ink placeholder:text-steel focus:border-brandorange focus:outline-none focus:ring-1 focus:ring-brandorange"
                  placeholder="Describe the problem or the work you'd like quoted"
                  value={form.details}
                  onChange={(e) => set({ details: e.target.value })}
                />
              </div>
              <div>
                <Label>Preferred days or times (optional)</Label>
                <Input
                  maxLength={300}
                  placeholder="e.g. weekday mornings, or after 5pm"
                  value={form.preferredTimes}
                  onChange={(e) => set({ preferredTimes: e.target.value })}
                />
              </div>
              {info?.referrer ? (
                <p className="rounded-lg bg-brandorange/10 px-3 py-2 text-xs text-brandorange-dark">
                  Sent by {info.referrer.name}. {info.referrer.friendOffer}
                </p>
              ) : null}
              {info?.askHowHeard && info.howHeardOptions.length ? (
                <div>
                  <Label>How did you hear about us? (optional)</Label>
                  <Select value={form.howHeard} onChange={(e) => set({ howHeard: e.target.value })}>
                    <option value="">Choose one…</option>
                    {info.howHeardOptions.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </Select>
                </div>
              ) : null}
              {/* Honeypot: visually hidden, skipped by keyboard and screen readers. */}
              <div className="absolute -left-[9999px] top-0" aria-hidden="true">
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={(e) => set({ website: e.target.value })}
                />
              </div>
              {error ? <p className="text-xs text-danger">{error}</p> : null}
              <Button type="submit" busy={busy} className="w-full">
                Send request
              </Button>
            </form>
          </Card>
        ) : null}

        <p className="text-center text-[10px] text-steel">Powered by Clevis</p>
      </div>
    </div>
  );
}
